/**
 * Wall geometry helpers used by slot extraction, candidate enumeration,
 * and placement code. Pure math; no I/O.
 *
 * `wallParam` is the 1D distance along the wall axis from
 * `wall.alignment.bottomLeft`, in meters.
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_ABS = Math.abs;
const MATH_ATAN2 = Math.atan2;
const { hypot2 } = require('../geometry/hypot2');

const { objectFootprint } = require('../geometry/objectFootprint');

/**
 * @returns {{wallDir, wallNormal, wallLen, start, end} | null}
 *   `wallDir` runs from bottomLeft to bottomRight; `wallNormal` points
 *   into the room. Returns null for degenerate / missing alignment.
 *
 * `wallNormal` is computed geometrically from the bottomLeft → bottomRight
 * direction. Floor polygons in this codebase are CCW (see CLAUDE.md), so
 * the inward perpendicular is the LEFT-side rotation of `wallDir`:
 * (dx, dz) → (-dz, dx). We don't trust `wall.rotation.y` for the normal
 * because some structures store the mesh's local rotation (which can be
 * parallel to the wall axis, yielding a wallNormal that runs *along* the
 * wall and pushes objects half-inside / half-outside the room).
 */
function getWallAxes(wall) {
  const start = wall?.alignment?.bottomLeft;
  const end = wall?.alignment?.bottomRight;
  if (!start || !end) return null;
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const wallLen = hypot2(dx, dz);
  if (wallLen <= 0) return null;
  const wallDir = { x: dx / wallLen, z: dz / wallLen };
  const wallNormal = { x: -dz / wallLen, z: dx / wallLen };
  // Y-rotation that aligns an object's local +Z with `wallNormal` (i.e.
  // makes the object's "front face" point into the room). Builders should
  // use this for the cabinet / appliance `rotation.y` instead of
  // `wall.rotation?.y` — the stored wall rotation isn't always the
  // inward-facing rotation for this codebase's data sources.
  const wallRotationY = MATH_ATAN2(wallNormal.x, wallNormal.z);
  return { wallDir, wallNormal, wallLen, wallRotationY, start, end };
}

/** Convert (wall, wallParam) to a world point on the wall surface. */
function wallParamToSurfacePoint(wall, wallParam) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  return {
    x: axes.start.x + wallParam * axes.wallDir.x,
    z: axes.start.z + wallParam * axes.wallDir.z,
  };
}

/** Project a world (x,z) point to its wallParam on the wall axis. */
function worldToWallParam(wall, point) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  return (
    (point.x - axes.start.x) * axes.wallDir.x +
    (point.z - axes.start.z) * axes.wallDir.z
  );
}

/**
 * Compute appliance world center given wall, wallParam, and depth.
 * Pushes the center inward by depth/2 so the back face sits flush
 * against the wall surface.
 */
function applianceCenterFromWallParam(wall, wallParam, depth, floorY, height) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  return {
    x:
      axes.start.x +
      wallParam * axes.wallDir.x +
      (depth / 2) * axes.wallNormal.x,
    y: floorY + height / 2,
    z:
      axes.start.z +
      wallParam * axes.wallDir.z +
      (depth / 2) * axes.wallNormal.z,
  };
}

/**
 * World footprint (4 corners) of a PLACED object, from its stored `rotation.y`.
 *
 * Kitchen builders store the rotation `getWallAxes` computes — `wallRotationY`,
 * which points the object's local +Z along the wall's inward normal. So local
 * +X maps to `(cos ry, -sin ry)` and local +Z to `(sin ry, cos ry)`.
 *
 * `geometry.calculateCorners(center, w, d, rotation)` uses the OPPOSITE
 * handedness. Handing it a stored `rotation.y` reflects the rectangle, which
 * costs nothing on an axis-aligned wall (the reflection maps the rectangle onto
 * itself) and is wrong by roughly 2θ on any other — a 24"-deep cabinet on a
 * 47-degree wall comes out spanning 33.6" and straddling the wall surface.
 * Non-orthogonal walls are exactly where wall-collision questions get asked, so
 * use this rather than passing a rotation to `calculateCorners`.
 *
 * @param {Object} obj - needs `center` {x, z} and `dimensions` {x, z}.
 * @returns {Array<{x, z}>|null}
 */
// Re-exported from `shared-lib/geometry/objectFootprint` so the one primitive
// serves every subsystem; kitchen callers keep importing it from here.

/**
 * How far `point` sits OUTBOARD of a peninsula `arm`'s wall line — the side
 * that arm's back panel skins — or null when the arm doesn't meet there.
 *
 * Measured to the arm's LINE, not to its endpoint, and signed. Both matter at
 * the corner where a run dies into an arm: the arm's own end may sit a
 * back-panel inset away from the corner point, and how far the run stands past
 * the line is exactly what decides whether the panel's strip is still free
 * (`materializeCandidate.backPanelStripsOnWall`) and what a slab still owes to
 * cap it (`buildCountertopRuns.extendRunIntoAbuttingPeninsula`).
 *
 * @param {{x:number,z:number}} point
 * @param {{wallDir:{x:number,z:number}}} wallAxes  Axes of the wall `point` is on.
 * @param {Object} arm  The peninsula wall to measure against.
 * @param {number} tolerance  How near the arm's line counts as meeting it.
 * @returns {number|null} Signed metres outboard, or null.
 */
function outboardOffsetFromArm(point, wallAxes, arm, tolerance) {
  const oa = getWallAxes(arm);
  if (!oa || !wallAxes) return null;
  const parallelism =
    oa.wallDir.x * wallAxes.wallDir.x + oa.wallDir.z * wallAxes.wallDir.z;
  if (MATH_ABS(parallelism) > 0.2) return null; // not perpendicular
  const along =
    (point.x - oa.start.x) * oa.wallDir.x +
    (point.z - oa.start.z) * oa.wallDir.z;
  if (along <= -tolerance || along >= oa.wallLen + tolerance) return null;
  const foot = {
    x: oa.start.x + along * oa.wallDir.x,
    z: oa.start.z + along * oa.wallDir.z,
  };
  const dx = point.x - foot.x;
  const dz = point.z - foot.z;
  if (hypot2(dx, dz) >= tolerance) return null; // not at this arm's line
  // Outboard is AWAY from the arm's inward normal: the face its panel skins.
  return dx * -oa.wallNormal.x + dz * -oa.wallNormal.z;
}

module.exports = {
  getWallAxes,
  outboardOffsetFromArm,
  wallParamToSurfacePoint,
  worldToWallParam,
  applianceCenterFromWallParam,
  objectFootprint,
};
